import { RefObject, useEffect, useRef } from 'react';

// Calls `onFrame` with a running clock, in seconds, on every animation frame while the
// target is on screen, the tab is visible and motion is allowed. With reduced motion it
// draws once, at `rest`: the moment chosen to show the whole picture.
export function useFrameLoop(target: RefObject<Element>, onFrame: (t: number) => void, rest: number) {
  const frameRef = useRef(onFrame);
  frameRef.current = onFrame;

  useEffect(() => {
    const el = target.current;
    if (!el) return undefined;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
    let raf = 0;
    let t = 0;
    let last = 0;
    let visible = false;

    const running = () => visible && !document.hidden && !reduce.matches;
    const frame = (now: number) => {
      t += Math.min(0.1, Math.max(0, (now - last) / 1000));
      last = now;
      frameRef.current(t);
      raf = running() ? requestAnimationFrame(frame) : 0;
    };
    const kick = () => {
      if (running()) {
        if (raf) return;
        last = performance.now();
        raf = requestAnimationFrame(frame);
        return;
      }
      cancelAnimationFrame(raf);
      raf = 0;
      if (reduce.matches) frameRef.current(rest);
    };

    frameRef.current(reduce.matches ? rest : 0);
    const seen = new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting;
      kick();
    });
    seen.observe(el);
    document.addEventListener('visibilitychange', kick);
    reduce.addEventListener?.('change', kick);
    return () => {
      cancelAnimationFrame(raf);
      seen.disconnect();
      document.removeEventListener('visibilitychange', kick);
      reduce.removeEventListener?.('change', kick);
    };
  }, [target, rest]);
}
